import React, { useState, useEffect } from 'react';
import { Brain, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { supabase } from '../../lib/supabase';
import AssessmentResponseAnalyzer from './ProfileHub/AssessmentResponseAnalyzer';

const AssessmentAnalysisPage = () => {
  const navigate = useNavigate();
  const { currentUser } = useFamilyContext();
  const [resultsPermalink, setResultsPermalink] = useState(null);

  // Load results permalink on mount
  useEffect(() => {
    if (currentUser?.id) {
      loadResultsPermalink();
    }
  }, [currentUser]);

  const loadResultsPermalink = async () => {
    try {
      const { data, error } = await supabase
        .from('assessment_responses')
        .select('results_permalink')
        .eq('user_id', currentUser.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (!error && data?.results_permalink) {
        setResultsPermalink(data.results_permalink);
      }
    } catch (error) {
      console.error('Error loading results permalink:', error);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Main Content - Two Column Layout */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex gap-8">
          {/* Left Column - Main Content */}
          <div className="flex-1 space-y-8">
            {/* Assessment Analysis Component */}
            <AssessmentResponseAnalyzer
              userId={currentUser?.id}
              onAnalysisComplete={(analysis) => {
                console.log('Assessment analysis completed:', analysis);
              }}
            />

            {/* Context Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* How This Helps Card */}
            <div className="bg-white border border-slate-200 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">How This Analysis Helps You</h3>
              <div className="space-y-3 text-sm text-slate-600">
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-violet-500 mt-2 flex-shrink-0"></div>
                  <p>Identifies your unique strengths and compelling storylines</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-violet-500 mt-2 flex-shrink-0"></div>
                  <p>Highlights areas where more detail would unlock better guidance</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-violet-500 mt-2 flex-shrink-0"></div>
                  <p>Generates personalized essay topics when quality threshold is met</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-violet-500 mt-2 flex-shrink-0"></div>
                  <p>Provides strategic insights for college positioning</p>
                </div>
              </div>
            </div>

            {/* Quality Threshold Card */}
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-emerald-900 mb-4">Response Quality Goals</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-emerald-700">Basic Insights</span>
                  <span className="text-sm font-semibold text-emerald-800">40%+</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-emerald-700">Detailed Analysis</span>
                  <span className="text-sm font-semibold text-emerald-800">60%+</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-emerald-700">Essay Topic Generation</span>
                  <span className="text-sm font-semibold text-emerald-800">70%+</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-emerald-700">Strategic Positioning</span>
                  <span className="text-sm font-semibold text-emerald-800">80%+</span>
                </div>
              </div>
              <div className="mt-4 p-3 bg-emerald-100 rounded-lg">
                <p className="text-xs text-emerald-800">
                  💡 The more specific details you share, the more personalized and actionable your guidance becomes.
                </p>
              </div>
            </div>
          </div>
          </div>

          {/* Right Sidebar - Quick Actions */}
          <div className="w-80 flex-shrink-0">
            <div className="sticky top-8">
              <div className="bg-white border border-slate-200 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">Quick Actions</h3>
                <div className="space-y-3">
                  {/* View Strategic Profile */}
                  {resultsPermalink && (
                    <button
                      onClick={() => navigate(`/college/strategic-profile/${resultsPermalink}`)}
                      className="w-full flex items-center gap-3 p-4 bg-teal-50 hover:bg-teal-100 border border-teal-200 rounded-lg transition-colors text-left"
                    >
                      <ExternalLink className="w-5 h-5 text-teal-600 flex-shrink-0" />
                      <div>
                        <div className="font-medium text-teal-900">View Strategic Profile</div>
                        <div className="text-sm text-teal-700">See your full assessment results</div>
                      </div>
                    </button>
                  )}

                  {/* Update Assessment */}
                  <button
                    onClick={() => navigate('/college/assessment')}
                    className="w-full flex items-center gap-3 p-4 bg-violet-50 hover:bg-violet-100 border border-violet-200 rounded-lg transition-colors text-left"
                  >
                    <Brain className="w-5 h-5 text-violet-600 flex-shrink-0" />
                    <div>
                      <div className="font-medium text-violet-900">Update Assessment</div>
                      <div className="text-sm text-violet-700">Add more details to your responses</div>
                    </div>
                  </button>

                  {/* Work on Essays */}
                  <button
                    onClick={() => navigate('/college/essays')}
                    className="w-full flex items-center gap-3 p-4 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-lg transition-colors text-left"
                  >
                    <div className="w-5 h-5 text-emerald-600 flex-shrink-0 font-bold">✍️</div>
                    <div>
                      <div className="font-medium text-emerald-900">Work on Essays</div>
                      <div className="text-sm text-emerald-700">Apply insights to essay development</div>
                    </div>
                  </button>

                  {/* Complete Profile */}
                  <button
                    onClick={() => navigate('/college/academic-profile')}
                    className="w-full flex items-center gap-3 p-4 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg transition-colors text-left"
                  >
                    <div className="w-5 h-5 text-blue-600 flex-shrink-0 font-bold">📊</div>
                    <div>
                      <div className="font-medium text-blue-900">Complete Profile</div>
                      <div className="text-sm text-blue-700">Build your comprehensive profile</div>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssessmentAnalysisPage;